import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Loader2, Sparkles, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELDS = [
  ["document_title", "Document title", "e.g. Driver's License"],
  ["issuing_authority", "Issuing authority", "e.g. Wisconsin DMV"],
  ["document_number", "Document number", ""],
  ["expiration_date", "Expiration date", "MM/DD/YYYY or blank"],
];
const LISTS = [["a", "List A — identity & work authorization"], ["b", "List B — identity"], ["c", "List C — work authorization"], ["none", "Not used on the I-9"]];

export default function IdentityApproveDialog({ employeeId, file, onClose, onApproved }) {
  const [details, setDetails] = useState({ document_title: "", issuing_authority: "", document_number: "", expiration_date: "", i9_list: "none" });
  const [extracting, setExtracting] = useState(false);
  const [confidence, setConfidence] = useState(null);
  const [saving, setSaving] = useState(false);
  const [imgUrl, setImgUrl] = useState(null);

  useEffect(() => {
    if (!file) return;
    let url;
    axios.get(`${API}/employees/${employeeId}/files/${file.id}?inline=true`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setImgUrl(url); })
      .catch(() => {});
    const seed = file.identity_details_suggested;
    if (seed) {
      setDetails({ ...seed, i9_list: seed.i9_list || "none" });
      setConfidence(seed.confidence || null);
    } else {
      extract();
    }
    return () => { if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file?.id]);

  const extract = async () => {
    setExtracting(true);
    try {
      const r = await axios.post(`${API}/employees/${employeeId}/files/${file.id}/extract-id`);
      setDetails((d) => ({ ...d, ...r.data, i9_list: r.data.i9_list || d.i9_list || "none" }));
      setConfidence(r.data.confidence || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Auto-read failed — enter the details manually");
    } finally {
      setExtracting(false);
    }
  };

  const approve = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/employees/${employeeId}/files/${file.id}/review`, {
        action: "approve",
        identity_details: { ...details, i9_list: details.i9_list === "none" ? null : details.i9_list },
      });
      toast.success("Approved — I-9 Section 2 details saved");
      onApproved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approval failed");
    } finally {
      setSaving(false);
    }
  };

  const isPdf = file?.content_type === "application/pdf";
  return (
    <Dialog open={!!file} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="identity-approve-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Verify &amp; approve — {file?.filename}
          </DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-5">
          <div className="rounded-lg bg-slate-100 border min-h-[260px] flex items-center justify-center overflow-hidden">
            {imgUrl ? (isPdf ? <iframe title="doc" src={imgUrl} className="w-full h-[340px]" /> : <img src={imgUrl} alt="Identity document" className="max-h-[340px] w-full object-contain" data-testid="identity-approve-preview" />)
              : <FileText className="w-8 h-8 text-slate-400" />}
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-600">Confirm these match the document. They pre-fill I-9 Section 2.</p>
              <Button size="sm" variant="outline" onClick={extract} disabled={extracting} className="gap-1 h-7 text-xs" data-testid="identity-reextract-btn">
                {extracting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3 text-purple-600" />} {extracting ? "Reading…" : "Re-read with AI"}
              </Button>
            </div>
            {confidence && <p className="text-[11px] text-slate-500" data-testid="identity-confidence">AI confidence: {confidence}</p>}
            {FIELDS.map(([k, label, ph]) => (
              <div key={k}>
                <Label className="text-xs">{label}</Label>
                <Input value={details[k] || ""} placeholder={ph} onChange={(e) => setDetails((d) => ({ ...d, [k]: e.target.value }))} data-testid={`identity-field-${k}`} />
              </div>
            ))}
            <div>
              <Label className="text-xs">I-9 Section 2 list</Label>
              <Select value={details.i9_list || "none"} onValueChange={(v) => setDetails((d) => ({ ...d, i9_list: v }))}>
                <SelectTrigger data-testid="identity-i9-list"><SelectValue /></SelectTrigger>
                <SelectContent>{LISTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={approve} disabled={saving || extracting} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="identity-approve-confirm">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Approve &amp; save details
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
